import { supabase } from '@/lib/supabase'
import type { CountStat, DashboardActivityItem, DashboardStatsSummary, DashboardTrend } from '../types'

const PERIOD_DAYS = 30

const STAR_RATING_MAP: Record<string, number> = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5,
}

const fallbackTimestamp = () => new Date().toISOString()

type QueryBuilder = any

const countRows = async (
  table: string,
  configure?: (query: QueryBuilder) => QueryBuilder
): Promise<number> => {
  try {
    let query = supabase.from(table).select('id', { count: 'exact' })
    if (configure) {
      query = configure(query)
    }
    const { count } = await query.limit(0)
    return count || 0
  } catch (error) {
    console.warn(`[dashboard] Failed to count ${table}`, error)
    return 0
  }
}

const calculateTrend = (current: number, previous: number): { trend: DashboardTrend; delta: number; changePercent: number } => {
  const delta = current - previous
  const changePercent = previous === 0 ? (current > 0 ? 100 : 0) : (delta / previous) * 100
  let trend: DashboardTrend = 'neutral'
  if (delta > 0) trend = 'up'
  if (delta < 0) trend = 'down'
  return {
    trend,
    delta,
    changePercent: Math.round(changePercent * 10) / 10,
  }
}

const buildCountStat = (total: number, current: number, previous: number): CountStat => {
  const { changePercent, delta, trend } = calculateTrend(current, previous)
  return {
    total,
    periodCount: current,
    previousPeriodCount: previous,
    delta,
    changePercent,
    trend,
  }
}

const getIsoForDaysAgo = (days: number) => {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString()
}

const safeMap = <T, R>(items: T[] | null | undefined, mapper: (value: T) => R): R[] =>
  Array.isArray(items) ? items.map(mapper) : []

const convertRatingToNumber = (rating: string | null) => {
  if (!rating) return 0
  return STAR_RATING_MAP[rating] || 0
}

const fetchTable = async <T>(promise: PromiseLike<{ data: T[] | null }>): Promise<T[]> => {
  try {
    const { data } = await promise
    return data || []
  } catch (error) {
    console.warn('[dashboard] Failed to load table data', error)
    return []
  }
}

export async function fetchDashboardStatsSummary(): Promise<DashboardStatsSummary> {
  const thirtyDaysAgo = getIsoForDaysAgo(PERIOD_DAYS)
  const sixtyDaysAgo = getIsoForDaysAgo(PERIOD_DAYS * 2)

  const [
    totalLeads,
    leadsCurrent,
    leadsPrevious,
    totalActiveGalleries,
    galleriesCurrent,
    galleriesPrevious,
  ] = await Promise.all([
    countRows('leads'),
    countRows('leads', (query) => query.gte('created_at', thirtyDaysAgo)),
    countRows('leads', (query) => query.gte('created_at', sixtyDaysAgo).lt('created_at', thirtyDaysAgo)),
    countRows('galleries', (query) => query.eq('is_public', true)),
    countRows('galleries', (query) => query.eq('is_public', true).gte('created_on', thirtyDaysAgo)),
    countRows('galleries', (query) =>
      query
        .eq('is_public', true)
        .gte('created_on', sixtyDaysAgo)
        .lt('created_on', thirtyDaysAgo)
    ),
  ])

  return {
    leads: buildCountStat(totalLeads, leadsCurrent, leadsPrevious),
    galleries: buildCountStat(totalActiveGalleries, galleriesCurrent, galleriesPrevious),
  }
}

export async function fetchDashboardActivity(limit = 8): Promise<DashboardActivityItem[]> {
  type LeadRecord = { id: string; client_name: string | null; service_requested: string | null; created_at: string | null }
  type GalleryRecord = { id: number; title: string | null; review_status: string | null; created_on: string | null }
  type ReviewRecord = { id: string; reviewer_name: string | null; star_rating: string | null; reviewer_comment: string | null; reply_comment: string | null; created_at: string | null }
  type SocialRecord = { id: string; caption: string | null; status: string | null; scheduled_time: string | null; created_at: string | null }

  const [leadRows, galleryRows, reviewRows, socialRows] = await Promise.all([
    fetchTable<LeadRecord>(
      supabase
        .from('leads')
        .select('id, client_name, service_requested, created_at')
        .order('created_at', { ascending: false })
        .limit(limit)
    ),
    fetchTable<GalleryRecord>(
      supabase
        .from('galleries')
        .select('id, title, review_status, created_on')
        .order('created_on', { ascending: false })
        .limit(limit)
    ),
    fetchTable<ReviewRecord>(
      supabase
        .from('gmb_reviews')
        .select('id, reviewer_name, star_rating, reviewer_comment, reply_comment, created_at')
        .order('created_at', { ascending: false })
        .limit(limit)
    ),
    fetchTable<SocialRecord>(
      supabase
        .from('social_posts')
        .select('id, caption, status, scheduled_time, created_at')
        .order('created_at', { ascending: false })
        .limit(limit)
    ),
  ])

  const leadActivity = safeMap<LeadRecord, DashboardActivityItem>(leadRows, (lead) => ({
    id: `lead-${lead.id}`,
    type: 'lead' as const,
    title: lead.client_name ? `Lead · ${lead.client_name}` : 'New website lead',
    description: lead.service_requested || 'New inquiry received',
    timestamp: lead.created_at || fallbackTimestamp(),
    status: 'new' as const,
    link: `/customers/leads?leadId=${lead.id}`,
  }))

  const galleryActivity = safeMap<GalleryRecord, DashboardActivityItem>(galleryRows, (gallery) => ({
    id: `gallery-${gallery.id}`,
    type: 'gallery' as const,
    title: gallery.title || 'Gallery update',
    description:
      gallery.review_status === 'approved'
        ? 'Gallery approved'
        : gallery.review_status === 'rejected'
          ? 'Gallery rejected'
          : 'Gallery submitted',
    timestamp: gallery.created_on || fallbackTimestamp(),
    status: gallery.review_status === 'approved' ? 'success' : 'info',
    link: `/media/galleries/${gallery.id}`,
  }))

  const reviewActivity = safeMap<ReviewRecord, DashboardActivityItem>(reviewRows, (review) => ({
    id: `review-${review.id}`,
    type: 'review' as const,
    title: review.reviewer_name ? `Review · ${review.reviewer_name}` : 'New review',
    description: `${convertRatingToNumber(review.star_rating).toFixed(1)}★ · ${review.reviewer_comment?.slice(0, 60) || 'New customer feedback'}`,
    timestamp: review.created_at || fallbackTimestamp(),
    status: review.reply_comment ? 'success' : 'warning',
    link: '/customers/reviews',
  }))

  const socialActivity = safeMap<SocialRecord, DashboardActivityItem>(socialRows, (post) => ({
    id: `social-${post.id}`,
    type: 'social' as const,
    title: 'Social post update',
    description: post.caption?.slice(0, 60) || 'Social post created',
    timestamp: post.created_at || post.scheduled_time || fallbackTimestamp(),
    status: post.status === 'SCHEDULED' ? 'scheduled' : post.status === 'FAILED' ? 'warning' : 'info',
    link: '/marketing/social',
  }))

  return [...leadActivity, ...galleryActivity, ...reviewActivity, ...socialActivity]
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
    .slice(0, limit)
}
